/**
 * @author W. Alex Livesley
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @date 2018-03-30
 * @description A class containing the error returned from a request to a GraphQL server where the request identifies 
 * one or more unknown resources.
 * @filename unknown-nodes-validation-error.ts
 */
import { Error } from "./error";

/**
 * A type defining the information identifying an unknown node.
 *
 * @type {{type:string, id:string}}
 */
export type UnknownNode = { type: string; id: string };

/**
 * The error returned from a request to a GraphQL server where the request identifies one or more unknown resources.
 *
 * @extends {Error}
 */
export class UnknownNodesValidationError extends Error {
  /**
   * The type ID of the error.
   *
   * @type {string}
   */
  public static readonly TYPE_ID: string = "oHUUARAvAkKNbVueCgKuiw";

  /**
   * A parametric constructor.
   *
   * @param {string} location The location where the error was thrown.
   *
   * @param {Array<UnknownNode>} _unknown The collection of unknown nodes.
   *
   * @param {string} id The ID of the error.
   */
  public constructor(
    location: string,
    private _unknown: UnknownNode[],
    id: string = null
  ) {
    super(location, id, 400, "Bad Request");
  }

  /**
   * Returns the details of the validation errors.
   *
   * @type {Array<UnknownNode>}
   */
  public unknown(): UnknownNode[] {
    return this._unknown;
  }

  /**
   * The type of the error - a general classification of the error.
   *
   * @type {string}
   */
  public get type(): string {
    return UnknownNodesValidationError.TYPE_ID;
  }
}
